import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, 
  MapPin, 
  Trophy, 
  Target, 
  Users,
  Award,
  Calendar,
  Video,
  Star,
  Heart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const YouthAthleteProfilePage = () => {
  const { athleteId } = useParams();
  const [athlete, setAthlete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchAthlete();
  }, [athleteId]);

  const fetchAthlete = async () => {
    try {
      setError(false);
      const response = await axios.get(`${API_URL}/api/youth-athletes/${athleteId}`);
      setAthlete(response.data);
    } catch (err) {
      console.error('Failed to fetch athlete:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading athlete profile...</p>
        </div>
      </div>
    );
  }

  if (error || !athlete) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Star className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-display text-xl uppercase mb-2">Athlete Not Found</h3>
          <p className="text-muted-foreground mb-4">This athlete profile doesn't exist or has been removed.</p>
          <Link to="/youth-athletes">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Athletes
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      {/* Hero Section with Athlete Photo */}
      <div className="relative">
        {/* Background gradient/pattern */}
        <div className="absolute inset-0 h-80 bg-gradient-to-br from-primary/20 via-secondary/10 to-background" />
        
        <div className="relative max-w-6xl mx-auto px-4 pt-8">
          <Link 
            to="/youth-athletes" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Youth Athletes
          </Link>

          <div className="flex flex-col lg:flex-row gap-8 items-start">
            {/* Athlete Photo & Logo */}
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="w-64 h-64 md:w-80 md:h-80 rounded-2xl overflow-hidden shadow-2xl border-4 border-background">
                  {athlete.photo_url ? (
                    <img 
                      src={athlete.photo_url} 
                      alt={athlete.name}
                      className="w-full h-full object-cover"
                      style={{
                        objectPosition: athlete.photo_position?.objectPosition || 'center'
                      }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary/30 to-secondary/30 flex items-center justify-center">
                      <span className="font-display font-bold text-8xl text-primary/50">
                        {athlete.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                {/* Logo overlay */}
                {athlete.logo_url && (
                  <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-background rounded-xl shadow-lg p-2">
                    <img 
                      src={athlete.logo_url} 
                      alt={`${athlete.name} logo`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Athlete Info */}
            <div className="flex-1 pt-4">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                {athlete.region && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm">{athlete.region}</span>
                  </div>
                )}
                {athlete.age && (
                  <Badge className="bg-primary/10 text-primary border-primary/20">
                    {athlete.age} years old
                  </Badge>
                )}
              </div>
              
              <h1 className="font-display font-black text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight text-foreground mb-4">
                {athlete.name}
              </h1>

              {athlete.tagline && (
                <p className="text-xl text-muted-foreground italic mb-4">
                  "{athlete.tagline}"
                </p>
              )}

              {/* Disciplines */}
              {athlete.disciplines?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {athlete.disciplines.map(d => (
                    <Badge key={d} className="text-sm px-3 py-1 bg-primary/10 text-primary border-primary/20">
                      {d}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Quick Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                {/* Years Competing */}
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <Calendar className="w-5 h-5 mx-auto mb-1 text-primary" />
                  {athlete.years_competing && athlete.years_competing > 0 ? (
                    <>
                      <p className="font-bold text-lg">{athlete.years_competing}+</p>
                      <p className="text-xs text-muted-foreground">Years Competing</p>
                    </>
                  ) : (
                    <>
                      <p className="font-bold text-sm leading-tight">Just Getting</p>
                      <p className="text-xs text-muted-foreground">Started!</p>
                    </>
                  )}
                </div>

                {/* Events Competed */}
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <Trophy className="w-5 h-5 mx-auto mb-1 text-amber-500" />
                  {athlete.events_competed && athlete.events_competed > 0 ? (
                    <>
                      <p className="font-bold text-lg">{athlete.events_competed}+</p>
                      <p className="text-xs text-muted-foreground">Events Competed</p>
                    </>
                  ) : (
                    <>
                      <p className="font-bold text-sm leading-tight">Building</p>
                      <p className="text-xs text-muted-foreground">Experience</p>
                    </>
                  )}
                </div>

                {/* Achievements */}
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <Award className="w-5 h-5 mx-auto mb-1 text-secondary" />
                  {athlete.achievements?.length > 0 ? (
                    <>
                      <p className="font-bold text-lg">{athlete.achievements.length}</p>
                      <p className="text-xs text-muted-foreground">Achievements</p>
                    </>
                  ) : (
                    <>
                      <p className="font-bold text-sm leading-tight">Working</p>
                      <p className="text-xs text-muted-foreground">Hard!</p>
                    </>
                  )}
                </div>
              </div>

              {/* CTA Button */}
              <Link to="/signup">
                <Button size="lg" className="rounded-full px-8">
                  <Heart className="w-5 h-5 mr-2" />
                  Support {athlete.name?.split(' ')[0]}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Bio & Story */}
          <div className="lg:col-span-2 space-y-8">
            {/* About Section */}
            <Card>
              <CardContent className="p-6 md:p-8">
                <h2 className="font-display font-bold text-2xl uppercase tracking-wide mb-4 flex items-center gap-2">
                  <Star className="w-6 h-6 text-primary" />
                  About {athlete.name?.split(' ')[0]}
                </h2>
                <div className="prose prose-lg max-w-none text-muted-foreground">
                  {athlete.full_bio ? (
                    <div dangerouslySetInnerHTML={{ __html: athlete.full_bio.replace(/\n/g, '<br />') }} />
                  ) : athlete.bio ? (
                    <p>{athlete.bio}</p>
                  ) : (
                    <p>More details coming soon...</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Their Story */}
            {athlete.story && (
              <Card>
                <CardContent className="p-6 md:p-8">
                  <h2 className="font-display font-bold text-2xl uppercase tracking-wide mb-4">
                    Their Journey
                  </h2>
                  <p className="text-lg text-muted-foreground">
                    {athlete.story}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Goals */}
            {athlete.goals && (
              <Card>
                <CardContent className="p-6 md:p-8">
                  <h2 className="font-display font-bold text-2xl uppercase tracking-wide mb-4 flex items-center gap-2">
                    <Target className="w-6 h-6 text-primary" />
                    Goals & Dreams
                  </h2>
                  <p className="text-lg text-muted-foreground italic">
                    "{athlete.goals}"
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Achievements & Info */}
          <div className="space-y-6">
            {/* Achievements */}
            {athlete.achievements?.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-display font-bold text-lg uppercase tracking-wide mb-4 flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    Achievements
                  </h3>
                  <div className="space-y-3">
                    {athlete.achievements.map((achievement, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <Award className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{achievement}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact/Social */}
            {(athlete.website || athlete.instagram || athlete.facebook) && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-display font-bold text-lg uppercase tracking-wide mb-4">
                    Connect
                  </h3>
                  <div className="space-y-3">
                    {athlete.website && (
                      <a 
                        href={athlete.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">🌐</span>
                        Website
                      </a>
                    )}
                    {athlete.instagram && (
                      <a 
                        href={`https://instagram.com/${athlete.instagram}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">📸</span>
                        @{athlete.instagram}
                      </a>
                    )}
                    {athlete.facebook && (
                      <a 
                        href={athlete.facebook} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">👤</span>
                        Facebook
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* CTA Card */}
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-6 text-center">
                <h3 className="font-display font-bold text-lg uppercase mb-2">
                  Join Our Community
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Support {athlete.name?.split(' ')[0]} and other rising stars by joining Elevated Roping Media.
                </p>
                <Link to="/signup">
                  <Button className="w-full rounded-full">
                    Get Started
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Media Gallery Section */}
        {athlete.media_gallery && athlete.media_gallery.length > 0 && (
          <div className="mt-12">
            <h2 className="font-display font-bold text-2xl uppercase tracking-wide mb-6 text-center flex items-center justify-center gap-2">
              <Video className="w-6 h-6 text-primary" />
              Highlights
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {athlete.media_gallery.slice(0, 3).map((media, idx) => (
                <Card key={idx} className="overflow-hidden group">
                  <div className="aspect-video relative bg-muted">
                    {media.type === 'video' ? (
                      <>
                        {media.url.includes('youtube.com') || media.url.includes('youtu.be') ? (
                          <iframe
                            src={`https://www.youtube.com/embed/${media.url.includes('youtu.be') 
                              ? media.url.split('/').pop() 
                              : new URL(media.url).searchParams.get('v')}`}
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : (
                          <video 
                            src={media.url} 
                            className="w-full h-full object-cover"
                            controls
                            poster={media.thumbnail}
                          />
                        )}
                      </>
                    ) : (
                      <img 
                        src={media.url} 
                        alt={media.caption || `Gallery image ${idx + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        style={{
                          objectPosition: media.position?.objectPosition || 'center'
                        }}
                      />
                    )}
                  </div>
                  {media.caption && (
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground">{media.caption}</p>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default YouthAthleteProfilePage;
